import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export const querySessionsByApps = createAsyncThunk('apps/querySessionsByApps', async (params, thunkAPI) => {
    try {
        const response = await axios.get(`${API_BASE}/analytics/by-apps`, { params });
        return response.data;
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
});

export const searchByAppNameAPI = createAsyncThunk('apps/searchByAppNameAPI', async ({ term, friendlyNames, aggregatorApps, stations, dateParams, limit, cursor, orderBy, orderDirection }, thunkAPI) => {
    try {
        const searchTerm = term.toLowerCase();
        let apps = [];
        if (searchTerm) {
            // Multi-station results
            const multiStationResults = Object.keys(friendlyNames)
                .filter(name => friendlyNames[name].toLowerCase().indexOf(searchTerm) >= 0);
            if (multiStationResults && multiStationResults.length > 0) {
                apps.push(...multiStationResults);
            }
            // Station results
            const stationResults = Object.values(stations)
                .filter(station => station.stationSchool.toLowerCase().indexOf(searchTerm) >= 0);
            if (stationResults && stationResults.length > 0) {
                apps.push(...stationResults.map(res => aggregatorApps[res.id]));
            }
            apps = apps.filter(Boolean);
            if (apps.length > 0) {
                const analyticsParams = { ...dateParams, limit, cursor, orderBy, orderDirection, appNameList: apps };
                const analyticsResponse = await axios.get(`${API_BASE}/analytics/by-apps`, { params: analyticsParams });
                return { apps, analytics: analyticsResponse.data };
            }
            return { apps: [], analytics: null };
        }
        // If no search term, just fetch all
        const analyticsParams = { ...dateParams, limit, cursor, orderBy, orderDirection };
        const analyticsResponse = await axios.get(`${API_BASE}/analytics/by-apps`, { params: analyticsParams });
        return { apps: [], analytics: analyticsResponse.data };
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
});

export const queryAppBreakdown = createAsyncThunk('apps/queryAppBreakdown', async ({ stationGroup, dateParams }, thunkAPI) => {
    try {
        const params = { ...dateParams, stationGroup };
        const response = await axios.get(`${API_BASE}/analytics/for-app`, { params });
        return { data: response.data, stationGroup };
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
});

// Action creators
export const showError = (state) => { state.error = true; };
export const loading = (state) => { state.loading = true; state.sessionsByApps.cursor = 0; };

const initialState = {
    loading: false,
    error: false,
    search: {
        term: '',
        appNameList: [],
    },
    sessionsByApps: {
        limit: 10,
        cursor: 0,
        allPages: 1,
        currentPage: 1,
        orderBy: 'sessions',
        orderDirection: 'desc',
        breakdown: {},
    },
};

const appsSlice = createSlice({
    name: 'apps',
    initialState,
    reducers: {
        setSearchTerm(state, action) {
            state.search.term = action.payload;
        },
        setLimit(state, action) {
            state.sessionsByApps.limit = action.payload;
            state.sessionsByApps.cursor = 0;
        },
        setOrderBy(state, action) {
            state.sessionsByApps.orderBy = action.payload;
        },
        setOrderDirection(state, action) {
            state.sessionsByApps.orderDirection = action.payload;
        },
        setCurrentPage(state, action) {
            state.sessionsByApps.currentPage = action.payload;
        },
        setBreakdown(state, action) {
            state.sessionsByApps.breakdown[action.payload.stationGroup] = action.payload.data;
        },
        nextPage(state, action) {
            if (action.payload && state.sessionsByApps.currentPage < state.sessionsByApps.allPages) {
                state.sessionsByApps.currentPage += 1;
                state.sessionsByApps.cursor = (state.sessionsByApps.currentPage - 1) * state.sessionsByApps.limit;
            }
        },
        prevPage(state, action) {
            if (action.payload && state.sessionsByApps.currentPage > 1) {
                state.sessionsByApps.currentPage -= 1;
                state.sessionsByApps.cursor = (state.sessionsByApps.currentPage - 1) * state.sessionsByApps.limit;
            }
        },
        openAppBreakdown(state, action) {
            const stationGroup = action.payload;
            if (!state.sessionsByApps.breakdown[stationGroup]) {
                state.sessionsByApps.breakdown[stationGroup] = { loading: true };
            }
        },
        closeAppBreakdown(state, action) {
            const stationGroup = action.payload;
            const { [stationGroup]: deleted, ...breakdown } = state.sessionsByApps.breakdown;
            state.sessionsByApps.breakdown = breakdown;
        },
        searchByAppName(state, action) {
            state.search.appNameList = action.payload;
        },
        toggleOrderBy(state, action) {
            const orderBy = action.payload;
            const { sessionsByApps } = state;
            sessionsByApps.orderBy = orderBy;
            sessionsByApps.cursor = 0;
            sessionsByApps.orderDirection = sessionsByApps.orderBy === orderBy
                ? sessionsByApps.orderDirection === 'desc'
                    ? 'asc'
                    : 'desc'
                : 'desc';
        },
        updateSearchedAppName(state, action) {
            state.search.term = action.payload;
        },
        resetPagination(state) {
            state.sessionsByApps = { ...initialState.sessionsByApps };
        },
        resetSearch(state) {
            state.search = { ...initialState.search };
        },
        resetAppList(state) {
            state.sessionsByApps.breakdown = {};
            state.loading = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(querySessionsByApps.pending, (state) => {
                state.loading = true;
            })
            .addCase(querySessionsByApps.fulfilled, (state, action) => {
                state.loading = false;
                const { data, meta = {} } = action.payload || {};
                const { cursor = 0, pages = 1, page = 1 } = meta;
                state.sessionsByApps = {
                    ...state.sessionsByApps,
                    data,
                    cursor,
                    allPages: pages,
                    currentPage: page,
                    breakdown: initialState.sessionsByApps.breakdown,
                };
            })
            .addCase(querySessionsByApps.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || true;
            })
            .addCase(searchByAppNameAPI.fulfilled, (state, action) => {
                // Optionally handle search results
            })
            .addCase(queryAppBreakdown.pending, (state) => {
                state.loading = true;
            })
            .addCase(queryAppBreakdown.fulfilled, (state, action) => {
                state.loading = false;
                const { data, stationGroup } = action.payload;
                state.sessionsByApps.breakdown[stationGroup] = {
                    loading: false,
                    data: data.data,
                };
            })
            .addCase(queryAppBreakdown.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || true;
            });
    },
});

// Thunk helpers (API)
export const toggleOrderByApi = (orderBy) => (dispatch, getState) => {
    dispatch(appsSlice.actions.toggleOrderBy(orderBy));
    const { sessions: { dateParams }, apps: { sessionsByApps, search } } = getState();
    dispatch(querySessionsByApps({
        ...dateParams,
        limit: sessionsByApps.limit,
        cursor: sessionsByApps.cursor,
        orderBy: sessionsByApps.orderBy,
        orderDirection: sessionsByApps.orderDirection,
        appNameList: search.appNameList,
    }));
    return null;
};

export const changeLimitApi = (newLimit) => (dispatch, getState) => {
    dispatch(appsSlice.actions.setLimit(newLimit));
    const { sessions: { dateParams }, apps: { sessionsByApps, search } } = getState();
    dispatch(querySessionsByApps({
        ...dateParams,
        limit: sessionsByApps.limit,
        cursor: sessionsByApps.cursor,
        orderBy: sessionsByApps.orderBy,
        orderDirection: sessionsByApps.orderDirection,
        appNameList: search.appNameList,
    }));
    return null;
};

export const toggleAppsBreakdown = (stationGroup) => (dispatch, getState) => {
    const { apps: { sessionsByApps: { breakdown } }, sessions: { dateParams } } = getState();
    const app = breakdown[stationGroup];
    if (!app || Object.keys(app).length === 0) {
        dispatch(queryAppBreakdown({ stationGroup, dateParams }));
        dispatch(appsSlice.actions.openAppBreakdown(stationGroup));
    } else {
        dispatch(appsSlice.actions.closeAppBreakdown(stationGroup));
    }
    return null;
};

export const nextPageApi = () => (dispatch, getState) => {
    const { apps: { sessionsByApps: { currentPage, allPages } }, sessions: { dateParams } } = getState();
    if (currentPage < allPages) {
        dispatch(appsSlice.actions.nextPage(true));
        const { apps: { sessionsByApps, search: searchState } } = getState();
        dispatch(querySessionsByApps({
            ...dateParams,
            limit: sessionsByApps.limit,
            cursor: sessionsByApps.cursor,
            orderBy: sessionsByApps.orderBy,
            orderDirection: sessionsByApps.orderDirection,
            appNameList: searchState.appNameList,
        }));
    }
    return null;
};

export const prevPageApi = () => (dispatch, getState) => {
    const { apps: { sessionsByApps: { currentPage } }, sessions: { dateParams } } = getState();
    if (currentPage > 1) {
        dispatch(appsSlice.actions.prevPage(true));
        const { apps: { sessionsByApps, search: searchState } } = getState();
        dispatch(querySessionsByApps({
            ...dateParams,
            limit: sessionsByApps.limit,
            cursor: sessionsByApps.cursor,
            orderBy: sessionsByApps.orderBy,
            orderDirection: sessionsByApps.orderDirection,
            appNameList: searchState.appNameList,
        }));
    }
    return null;
};

export const { setSearchTerm, setLimit, setOrderBy, setOrderDirection, setCurrentPage, setBreakdown, nextPage, prevPage, openAppBreakdown, closeAppBreakdown, searchByAppName, toggleOrderBy, updateSearchedAppName, resetPagination, resetSearch, resetAppList } = appsSlice.actions;
export default appsSlice.reducer;

// Selectors
export const selectAppsLoading = (state) => state.apps.loading;
export const selectSessionsByApps = (state) => state.apps.sessionsByApps;
export const selectAppsSearch = (state) => state.apps.search;
export const selectAppsError = (state) => state.apps.error;
export const selectAppsState = (state) => state.apps; 